import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";

function mdBlocks(md = "") {
  const lines = md.split("\n");
  const out = [];
  let para = [];
  let bullets = [];
  const flushP = () => { if (para.length) { out.push({ t: "p", v: para.join(" ").trim() }); para = []; } };
  const flushB = () => { if (bullets.length) { out.push({ t: "ul", v: [...bullets] }); bullets = []; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushP(); flushB(); continue; }
    if (line.startsWith("### ")) { flushP(); flushB(); out.push({ t: "h3", v: line.slice(4) }); continue; }
    if (line.startsWith("## ")) { flushP(); flushB(); out.push({ t: "h2", v: line.slice(3) }); continue; }
    if (line.startsWith("- ")) { flushP(); bullets.push(line.slice(2)); continue; }
    flushB(); para.push(line);
  }
  flushP(); flushB();
  return out;
}

export function BriefingsIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/briefings").then((r) => setItems(r.data || [])); }, []);
  return (
    <PublicLayout>
      <section className="border-b border-[var(--ink)] bg-[var(--parchment)] py-16 sm:py-20" data-testid="briefings-index">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="eyebrow">Executive Briefings</div>
          <h1 className="font-display font-black text-[clamp(2.4rem,6vw,5rem)] mt-4 leading-[0.95] tracking-tight">
            Conversations from the desk.
          </h1>
          <p className="mt-6 font-display italic text-2xl text-[var(--ink-soft)] max-w-3xl">
            Strategic intelligence pieces — concise, source-aware, and decision-coded. No music. No noise.
          </p>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((b) => (
              <Link key={b.slug} to={`/briefings/${b.slug}`} className="block bg-white border border-[var(--border-soft)] hover-lift overflow-hidden" data-testid={`briefing-card-${b.slug}`}>
                {b.hero_image && <div className="aspect-[16/10] overflow-hidden"><img src={b.hero_image} alt={b.title} className="w-full h-full object-cover" /></div>}
                <div className="p-5">
                  <div className="eyebrow">Briefing · {b.read_minutes || 6} min</div>
                  <h3 className="font-display font-bold text-xl mt-2 leading-tight text-[var(--ink)] hover:text-[var(--oxblood)]">{b.title}</h3>
                  <p className="mt-2 text-sm text-[var(--ink-soft)] leading-relaxed">{b.dek}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export function BriefingDetail() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  useEffect(() => { api.get(`/briefings/${slug}`).then((r) => setB(r.data)).catch(() => {}); }, [slug]);
  if (!b) return <PublicLayout><div className="max-w-[1400px] mx-auto px-6 py-20 font-mono text-sm">Loading…</div></PublicLayout>;
  const blocks = mdBlocks(b.body_md || "");
  return (
    <PublicLayout>
      <article className="bg-[var(--parchment)]" data-testid="briefing-detail-page">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-10 border-b border-[var(--ink)]">
          <div className="max-w-[820px] mx-auto fade-up">
            <Link to="/briefings" className="eyebrow hover:text-[var(--ink)]">← All briefings</Link>
            <div className="mt-4 eyebrow flex items-center gap-3"><span>Executive Briefing</span><span className="text-[var(--ink-soft)]">·</span><span className="text-[var(--ink-soft)]">{b.read_minutes || 6} min read</span></div>
            <h1 className="font-display font-black text-[clamp(2.2rem,5vw,4rem)] mt-5 leading-[0.98] tracking-tight" data-testid="briefing-title">{b.title}</h1>
            {b.dek && <p className="mt-5 font-display italic text-2xl text-[var(--ink-soft)] leading-snug">{b.dek}</p>}
          </div>
        </div>
        <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
          {b.hero_image && <div className="mb-10 overflow-hidden"><img src={b.hero_image} alt={b.title} className="w-full aspect-[16/9] object-cover" /></div>}
          <div className="prose-editorial">
            {blocks.map((blk, i) => {
              if (blk.t === "p" && i === 0) return <p key={i} className="dropcap">{blk.v}</p>;
              if (blk.t === "p") return <p key={i}>{blk.v}</p>;
              if (blk.t === "h2") return <h2 key={i}>{blk.v}</h2>;
              if (blk.t === "h3") return <h3 key={i}>{blk.v}</h3>;
              if (blk.t === "ul") return <ul key={i}>{blk.v.map((li, j) => <li key={j}>{li}</li>)}</ul>;
              return null;
            })}
          </div>
          <div className="mt-14 pt-10 border-t border-[var(--border-soft)] bg-[var(--ink)] text-[var(--parchment)] p-7 sm:p-10 -mx-4 sm:-mx-6">
            <div className="grid sm:grid-cols-2 gap-6 items-center">
              <div>
                <div className="eyebrow" style={{ color: "var(--brass)" }}>Continue the thread</div>
                <h3 className="font-display font-bold text-2xl sm:text-3xl mt-2 leading-tight">Bring this briefing into a real decision.</h3>
              </div>
              <div className="flex flex-col sm:items-end gap-3">
                <Link to="/strategy-session" className="btn-primary" data-testid="briefing-cta-schedule">Schedule a Strategy Session <ArrowRight size={14} /></Link>
                <Link to="/luminous" className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--brass)] hover:underline">Or generate a related brief →</Link>
              </div>
            </div>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
